import { outgoingParts } from './outgoing';
import { v7 } from 'uuid';
import { NetworkError, ProblemError, type HttpClient, type Id, type MediaService, type OutgoingDraft } from '@/model';
import { mediaKind, type CoreMediaService } from './media';
import { decodeMessage, object } from './decode';
import { AccountStore, mergeMessage, type OutboxRow } from './store';

export class Outbox {
  private running = false;
  private closed = false;
  private paused = false;
  private flight?: Promise<void>;
  private timer?: ReturnType<typeof setTimeout>;
  private readonly active = new Map<Id, AbortController>();
  constructor(private readonly db: AccountStore, private readonly http: HttpClient, private readonly media: MediaService & Partial<Pick<CoreMediaService, 'uploadAttachment'>>) {}
  async enqueue(conversationId: Id, draft: OutgoingDraft, forwardOf?: Id): Promise<{ clientId: Id }> {
    if (this.closed) throw new NetworkError('This outbox session has ended');
    if (await this.db.erasedConversations.get(conversationId)) throw new ProblemError({ status: 404, code: 'not_found' });
    const clientId = draft.clientId ?? v7();
    draft = structuredClone(draft);
    if ((!outgoingParts(draft).length || !draft.text?.trim() && !draft.cards?.length && !draft.links?.length) && !draft.attachments?.length && !forwardOf) throw new ProblemError({ status: 400, code: 'invalid_request' });
    draft = { ...draft, ...(draft.attachments ? { attachments: draft.attachments.map(a => ({ ...a, kind: mediaKind(a.file.type, a.kind) })) } : {}) };
    const capturedDraft = draft.expectedDraftUpdatedAt === undefined ? undefined : await this.db.drafts.get(conversationId);
    const capturedIds = new Set<string>();
    if (capturedDraft?.updatedAt === draft.expectedDraftUpdatedAt) {
      const available = [...(capturedDraft?.attachments ?? [])];
      for (const attachment of draft.attachments ?? []) for (const [index, row] of available.entries()) if (attachmentSignature(row.attachment) === attachmentSignature(attachment) && await sameBytes(row.attachment.file, attachment.file)) { capturedIds.add(row.id); available.splice(index, 1); break; }
    }
    if (this.closed) throw new NetworkError('This outbox session has ended');
    await this.db.transaction('rw', this.db.tables, async () => {
      if (this.closed) throw new NetworkError('This outbox session has ended');
      if (await this.db.erasedConversations.get(conversationId)) throw new ProblemError({ status: 404, code: 'not_found' });
      const existing = await this.db.outbox.get(clientId);
      if (existing) { if (existing.conversation_id !== conversationId || draftSignature(existing.draft) !== draftSignature(draft)) throw new ProblemError({ status: 409, code: 'idempotency_conflict' }); return; }
      if (await this.db.messages.where('[sender_id+client_id]').equals([this.db.accountId, clientId]).first()) return;
      await this.db.outbox.put({ client_id: clientId, conversation_id: conversationId, draft, created_at: Date.now(), state: 'sending', attempts: 0, next_attempt_at: 0, uploaded: [], ...(forwardOf ? { forward_of: forwardOf } : {}) });
      const source = await this.db.drafts.get(conversationId);
      if (source && draft.expectedDraftUpdatedAt !== undefined && source.updatedAt === draft.expectedDraftUpdatedAt) {
        const textMatches = source.text === (draft.text ?? '') && source.replyToId === draft.replyToId;
        await this.db.drafts.put({ ...source, ...(textMatches ? { text: '', replyToId: undefined, editingMessageId: undefined } : {}), attachments: source.attachments?.filter(row => !capturedIds.has(row.id)), updatedAt: Math.max(Date.now(), source.updatedAt + 1), local_revision: (source.local_revision ?? 0) + 1, source: 'local', local: true });
      }
    });
    this.schedule(0); return { clientId };
  }
  start(): void { this.closed = false; this.running = true; globalThis.addEventListener?.('online', this.wake); this.schedule(0); }
  async stop(): Promise<void> {
    this.closed = true; this.running = false; globalThis.removeEventListener?.('online', this.wake);
    if (this.timer) clearTimeout(this.timer); this.timer = undefined;
    for (const abort of this.active.values()) abort.abort();
    // A request can itself trigger session retirement. Waiting for that request here would deadlock
    // refresh -> sign-out -> outbox.stop -> refresh. All result/progress paths are fenced by running.
  }
  setPaused(paused: boolean): void { this.paused = paused; if (!paused) this.schedule(0); }
  private wake = () => { void this.db.outbox.where('state').equals('sending').modify({ next_attempt_at: 0 }).then(() => this.schedule(0)).catch(() => undefined); };
  async retry(clientId: Id): Promise<void> { const row = await this.db.outbox.get(clientId); if (row) { await this.db.outbox.update(clientId, { state: 'sending', failure: undefined, next_attempt_at: 0, attempts: 0 }); this.schedule(0); } }
  async discard(clientId: Id): Promise<void> {
    this.active.get(clientId)?.abort();
    const sessions = await this.db.mediaUploads.where('key').startsWith(`${clientId}:`).toArray();
    await this.db.transaction('rw', this.db.outbox, this.db.mediaUploads, async () => { await this.db.outbox.delete(clientId); await this.db.mediaUploads.bulkDelete(sessions.map(s => s.key)); });
    for (const session of sessions) void this.http.delete(`/v1/media/uploads/${session.upload_id}`).catch(() => undefined);
    this.schedule(0);
  }
  private schedule(delay: number) {
    if (!this.running || this.paused) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = undefined; void this.pump(); }, delay);
  }
  private async pump(): Promise<void> {
    if (!this.running || this.paused || this.flight) return;
    const run = async () => {
      while (this.running && !this.paused && globalThis.navigator?.onLine !== false) {
        const rows = await this.db.outbox.orderBy('created_at').toArray();
        const heads = rows.filter((row, i) => !rows.slice(0, i).some(prior => prior.conversation_id === row.conversation_id));
        const ready = heads.find(row => row.state === 'sending' && row.next_attempt_at <= Date.now());
        if (!ready) {
          const next = heads.filter(row => row.state === 'sending').map(row => row.next_attempt_at);
          if (next.length) this.schedule(Math.max(50, Math.min(...next) - Date.now()));
          return;
        }
        if (!this.running || this.paused) return;
        await this.attempt(ready);
      }
    };
    const lockedRun = async () => {
      if (globalThis.navigator?.locks) await navigator.locks.request(`hylo.web.outbox:${this.db.accountId}`, { ifAvailable: true }, async lock => { if (lock) await run(); else this.schedule(1000); });
      else await run();
    };
    this.flight = lockedRun().catch(() => { if (this.running) this.schedule(1000); }).finally(() => { this.flight = undefined; });
    await this.flight;
  }
  private async attempt(row: OutboxRow): Promise<void> {
    if (!this.running || this.paused) return;
    const abort = new AbortController(); this.active.set(row.client_id, abort);
    const generation = (await this.db.meta.get('sync'))?.generation;
    try {
      const parts = outgoingParts(row.draft);
      const attachments = row.draft.attachments ?? [];
      for (const [index, attachment] of attachments.entries()) {
        if (!this.running || this.paused || abort.signal.aborted) return;
        let mediaId = row.uploaded[index];
        if (!mediaId) {
          const uploadOptions = { kind: attachment.kind, name: attachment.name, meta: { width: attachment.width, height: attachment.height, duration_ms: attachment.durationMs, waveform: attachment.waveform }, signal: abort.signal, onProgress: (sent: number, total: number) => { if (!this.running || abort.signal.aborted) return; const progress = (index + (total > 0 ? sent / total : 0)) / Math.max(1, attachments.length); void this.db.outbox.update(row.client_id, { progress }).catch(() => undefined); } };
          const item = this.media.uploadAttachment ? await this.media.uploadAttachment(attachment.file, uploadOptions, `${row.client_id}:${index}`) : await this.media.upload(attachment.file, uploadOptions);
          mediaId = item.id; row.uploaded[index] = mediaId;
          if (!this.running || abort.signal.aborted) return;
          await this.db.outbox.update(row.client_id, { uploaded: row.uploaded });
        }
        parts.push({ type: 'media', media_id: mediaId, kind: attachment.kind, ...(attachment.name ? { name: attachment.name } : {}) });
      }
      if (!this.running || this.paused || abort.signal.aborted || !await this.db.outbox.get(row.client_id)) return;
      const response = object(await this.http.post(`/v1/conversations/${row.conversation_id}/messages`, { client_id: row.client_id, parts, ...(row.draft.replyToId ? { reply_to_id: row.draft.replyToId } : {}), ...(row.draft.mentions?.length ? { mentions: row.draft.mentions } : {}), ...(row.forward_of ? { forward_of: row.forward_of } : {}), ...(row.draft.addressedTo ? { addressed_to: row.draft.addressedTo } : {}) }, { signal: abort.signal }));
      if (!this.running || abort.signal.aborted) return;
      const message = decodeMessage(response.message);
      await this.db.transaction('rw', this.db.tables, async () => {
        if (!this.running || this.paused || (await this.db.meta.get('sync'))?.generation !== generation || !await this.db.outbox.get(row.client_id)) return;
        await mergeMessage(this.db, message); await this.db.outbox.delete(row.client_id);
      });
      // If resync overtook this answer, the retained row retries the identical client_id after the fence.
    } catch (error) {
      if (!this.running || abort.signal.aborted) return;
      const attempts = row.attempts + 1;
      const transient = error instanceof NetworkError || !(error instanceof ProblemError) || error.status === 429 || error.status >= 500;
      const delay = Math.max(error instanceof ProblemError ? (error.retryAfter ?? 0) * 1000 : 0, Math.min(60000, 500 * 2 ** Math.min(attempts, 7)) * (.75 + Math.random() * .5));
      await this.db.outbox.update(row.client_id, { attempts, state: transient ? 'sending' : 'failed', failure: error instanceof ProblemError ? error.code : 'network', rate_limited_until: error instanceof ProblemError && error.status === 429 ? Date.now() + delay : undefined, next_attempt_at: Date.now() + delay });
    } finally { this.active.delete(row.client_id); }
  }
}


function attachmentSignature(attachment: NonNullable<OutgoingDraft['attachments']>[number]): string {
  const { file, ...rest } = attachment; return JSON.stringify({ ...rest, bytes: file.size, mime: file.type });
}
function draftSignature(draft: OutgoingDraft): string {
  const { clientId: _clientId, expectedDraftUpdatedAt: _expected, attachments, ...rest } = draft;
  return JSON.stringify({ ...rest, attachments: attachments?.map(attachmentSignature) ?? [] });
}

async function sameBytes(a: Blob, b: Blob): Promise<boolean> {
  if (a.size !== b.size || a.type !== b.type) return false;
  for (let offset = 0; offset < a.size; offset += 1024 * 1024) {
    const [left, right] = await Promise.all([a.slice(offset, offset + 1024 * 1024).arrayBuffer(), b.slice(offset, offset + 1024 * 1024).arrayBuffer()]);
    const bytes = new Uint8Array(left); const other = new Uint8Array(right);
    if (bytes.some((value, index) => value !== other[index])) return false;
  }
  return true;
}
